import React, {
  useContext,
  createContext,
  ReactNode,
  useRef,
  useState,
  useEffect,
} from "react";
import { LocalStorageVariables } from "../common/entities/type";
import {
  AffiliateUserType,
  UserRole,
  UserType,
} from "../common/entities/profile";
import { platformActions } from "../modules/platform/store/platform-slice";
import { answersActions } from "../modules/questionnaire/store/answers-slice";
import { useDispatch } from "react-redux";
import { setCustomerSession } from "../common/network/customerSession";
import {
  markLoggingOut,
  runSessionTeardowns,
} from "../common/utils/sessionTeardown";
const AuthContext = createContext({});

type PropsType = {
  children: ReactNode;
};
const AuthProvider = ({ children }: PropsType) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserType | AffiliateUserType | null>(null);
  const dispatch = useDispatch();

  // DERIVED, never stored. This used to be its own useState(false) that each
  // auth path had to remember to set, and signup didn't: it called AuthSetUser
  // and then navigate()d client-side, so the mount effect below (the only other
  // writer) never re-ran and the flag stayed false for the whole session. A
  // role-1 customer then took every affiliate branch — the wrapper skipped its
  // mobile guard, Breadcrumb fetched GetCustomers with a customer token (401),
  // and the dashboard read reportData.dashboard on data only the simple-user
  // branch loads, crashing. Deriving it means `user` is the single source of
  // truth and no future caller can desync it.
  const isSimpleUser = user?.role === UserRole.Customer;

  // const loadUser = async () => {
  //   let usr = localStorage.getItem(LocalStorageVariables.USER);
  //   if (usr !== null) {
  //     setUser(JSON.parse(usr));
  //   }
  // };

  const saveUser = async (newUser: any) => {
    setUser(newUser);
    localStorage.setItem(LocalStorageVariables.USER, JSON.stringify(newUser));

    return newUser;
  };

  const isUserLoaded = () => {
    return user !== null;
  };

  /**
   * Centralized logout function that clears all auth state and broadcasts to other tabs
   * @param options.redirect - Whether to redirect after logout (default: true)
   * @param options.redirectUrl - URL to redirect to (default: "/")
   */
  const logout = (options?: { redirect?: boolean; redirectUrl?: string }) => {
    const { redirect = true, redirectUrl = "/" } = options || {};

    // Tell the API layer a teardown is in progress *before* touching
    // localStorage, so requests still resolving during it can't re-seed
    // redirect_url or write their stale user snapshot back (see
    // sessionTeardown). Only for redirecting logouts: those always end in a full
    // page load, which resets the flag. A non-redirecting logout leaves this
    // document alive and needing the API layer to behave normally.
    if (redirect) {
      markLoggingOut();
    }

    // Set loading to prevent flicker
    setIsLoading(true);

    // Clear auth state (isSimpleUser follows from user being null)
    setUser(null);

    // Clear Redux state
    dispatch(platformActions.clearPlatformState());
    dispatch(answersActions.clearState());

    // Remove items from localStorage
    localStorage.removeItem(LocalStorageVariables.USER);

    // Customer session is per-tab (sessionStorage); the legacy localStorage
    // removal stays for sessions started before the sessionStorage migration.
    setCustomerSession(null);
    localStorage.removeItem(LocalStorageVariables.COSTUMER);
    localStorage.removeItem(LocalStorageVariables.NOTES);
    localStorage.removeItem(LocalStorageVariables.ENVIRONMENT);
    localStorage.removeItem(LocalStorageVariables.REDIRECT_URL);
    // Remembered listing filters and in-memory caches (History lists) are one
    // user's view of their own data.
    localStorage.removeItem(LocalStorageVariables.HISTORY_FILTERS);
    localStorage.removeItem(LocalStorageVariables.TIMELINE_PREFERENCES);
    runSessionTeardowns();

    // Broadcast logout event to all open tabs
    localStorage.setItem("logout-event", Date.now().toString());
    localStorage.removeItem("logout-event");

    // Redirect if requested (keep loading state true to prevent PrivateRoute from setting redirect_url)
    if (redirect) {
      window.location.href = redirectUrl;
    } else {
      // Only reset loading state if not redirecting
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const user = localStorage.getItem(LocalStorageVariables.USER);
    if (user === null) {
      setIsLoading(false);
      return;
    }
    const curUser: UserType | AffiliateUserType = JSON.parse(user);
    setUser(curUser);
    // No artificial delay: these updates batch into a single render, so
    // consumers never observe "not loading" with a null user.
    setIsLoading(false);
  }, []);

  /**
   * Broadcast a login event to all other tabs. Mirrors the logout-event
   * pattern below: write+remove a localStorage key so the `storage`
   * listener fires in OTHER tabs (same-tab `setItem` does not trigger
   * the event). Other tabs respond by reloading — `AuthProvider`'s
   * mount effect reads the fresh user from localStorage on the new
   * page, and `AuthRoute` / `PrivateRoute` route to the right place.
   *
   * Callers (`SignInForm`, `SignUpForm`) should invoke this AFTER all
   * supplementary user-info fetches finish, so localStorage has the
   * complete user object (with questionnaire flags etc.) by the time
   * other tabs reload.
   */
  const broadcastLogin = () => {
    localStorage.setItem("login-event", Date.now().toString());
    localStorage.removeItem("login-event");
  };

  // Listen for login/logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Detect logout event broadcast from another tab
      if (e.key === "logout-event") {
        // Clear state and reload the page. The customer session is per-tab
        // (sessionStorage), so each tab must clear its own copy here — the
        // broadcasting tab can't do it for us.
        setCustomerSession(null);
        dispatch(platformActions.clearPlatformState());
        window.location.href = "/";
      }
      // Detect login event broadcast from another tab. Reload so this
      // tab picks up the new user from localStorage; AuthRoute (on the
      // /auth page) and PrivateRoute (on protected routes) will resolve
      // the right destination once the new user state is loaded.
      if (e.key === "login-event") {
        window.location.reload();
      }
    };

    // Add storage event listener
    window.addEventListener("storage", handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  const value = {
    AuthIsLoading: isLoading,
    AuthIsUserLoaded: isUserLoaded,
    AuthUser: user,
    AuthSetUser: saveUser,
    AuthIsSimpleUser: isSimpleUser,
    AuthSetIsLoading: setIsLoading,
    AuthLogout: logout,
    AuthBroadcastLogin: broadcastLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

type ContextValueType = {
  AuthIsLoading: boolean;
  AuthIsUserLoaded: Function;
  AuthUser: UserType;
  AuthSetUser: Function;
  // Derived from AuthUser.role — there is deliberately no setter. See the note
  // at the derivation in AuthProvider.
  AuthIsSimpleUser: boolean;
  AuthSetIsLoading: Function;
  AuthLogout: (options?: { redirect?: boolean; redirectUrl?: string }) => void;
  AuthBroadcastLogin: () => void;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AppStoreProvider");
  }

  return context as ContextValueType;
};
